/*
 * Exercise A question generator (FR-EXA-01).
 *
 * Responsibilities (this module only):
 *   - Assemble a single Exercise A multiple-choice question from a
 *     caller-supplied target rune: pull distractors via FR-LOGIC-03's
 *     `pickDistractors`, combine them with the target, shuffle for
 *     display, and report where the target landed after the shuffle.
 *   - Honour the "minimum 2 total options" floor implicitly: when the
 *     eligible pool yields at least one distractor we ship target +
 *     distractors; when the pool is empty we still return a 1-option
 *     question (target alone). The 2-option floor is enforced at the
 *     content layer (Level 1 always exposes at least 5 runes), so
 *     "target alone" should be unreachable in practice — we don't
 *     special-case it here.
 *
 * Scope boundary: this generator builds *one* question for *one*
 * caller-supplied target. Session composition (FR-LOGIC-01's 75/25 mix,
 * FR-LOGIC-02's "every current-level rune appears at least once") is
 * the session runner's job (#22/#23), not this module's.
 *
 * Pure function: no module-level state, no `getCorpus()` calls. The
 * corpus, current level, and target all come from the caller. RNG is
 * injected via `options.random` and forwarded to `pickDistractors` so a
 * single seeded RNG drives both layers — that keeps the deterministic
 * tests below short.
 */

import type { RuneEntry } from './csv';
import { pickDistractors } from './distractors';

export interface ExerciseAQuestion {
  /** The rune the user must identify. */
  target: RuneEntry;
  /**
   * Options as displayed, including the target. Length is between 1
   * and `totalOptions` (default 4). In practice the floor is 2 once
   * the level-1 content is loaded; see module docstring.
   */
  options: RuneEntry[];
  /** Position of `target` within `options` after the shuffle. */
  correctIndex: number;
}

export interface GenerateExerciseAOptions {
  /**
   * Total max options including the target. Must be an integer in
   * [2, 4]. Out-of-range or non-integer values fall back silently to
   * the default. Default 4.
   */
  totalOptions?: number;
  /** Injectable RNG returning a float in [0, 1). Default Math.random. */
  random?: () => number;
}

const DEFAULT_TOTAL_OPTIONS = 4;
const MIN_TOTAL_OPTIONS = 2;

/**
 * Coerce a caller-supplied `totalOptions` into the allowed range. We
 * silently fall back rather than throw because the only legitimate
 * callers are internal — a bad value coming through is a programmer
 * error, not a runtime condition we want to surface to the user. The
 * floor of 2 mirrors the AC's minimum-options rule (an MCQ with one
 * option isn't an MCQ); the ceiling is the design's option-tile count.
 */
function sanitizeTotalOptions(raw: number | undefined): number {
  if (raw === undefined) return DEFAULT_TOTAL_OPTIONS;
  if (!Number.isInteger(raw)) return DEFAULT_TOTAL_OPTIONS;
  if (raw < MIN_TOTAL_OPTIONS || raw > DEFAULT_TOTAL_OPTIONS) return DEFAULT_TOTAL_OPTIONS;
  return raw;
}

/**
 * Build one Exercise A question for the given target rune.
 *
 * The caller picks the target; this generator only handles option
 * assembly:
 *   1. Delegate distractor sampling to `pickDistractors`, which returns
 *      0..(totalOptions - 1) entries from the level-eligible pool with
 *      the target excluded.
 *   2. Concatenate `[target, ...distractors]`.
 *   3. Shuffle the combined array in place using a Fisher-Yates pass
 *      driven by the same injected RNG.
 *   4. Recover `correctIndex` by reference-equality search — the target
 *      object identity is preserved through the spread.
 *
 * The function is pure: same inputs + same `random` produce the same
 * output every time.
 */
export function generateExerciseA(
  target: RuneEntry,
  corpus: readonly RuneEntry[],
  currentLevel: number,
  options?: GenerateExerciseAOptions,
): ExerciseAQuestion {
  const totalOptions = sanitizeTotalOptions(options?.totalOptions);
  const random = options?.random ?? Math.random;

  const distractors = pickDistractors(target, corpus, currentLevel, {
    totalOptions,
    random,
  });

  const combined: RuneEntry[] = [target, ...distractors];
  shuffleInPlace(combined, random);

  // Identity comparison is safe: pickDistractors never returns the
  // target (it excludes by `latin`), and the target reference is the
  // same object we spread in above.
  const correctIndex = combined.indexOf(target);

  return { target, options: combined, correctIndex };
}

/**
 * Standard Fisher-Yates shuffle, in place. Iterates from the last index
 * down to 1, swapping each element with a uniformly random earlier-or-
 * equal slot. With `random()` in [0, 1), `Math.floor(random() * (i + 1))`
 * lands in [0, i], which is the correct inclusive range for a uniform
 * shuffle.
 */
function shuffleInPlace<T>(array: T[], random: () => number): void {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const tmp = array[i]!;
    array[i] = array[j]!;
    array[j] = tmp;
  }
}
